import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, UserPlus, Loader2 } from "lucide-react";
import { artistsMgmtApi } from "./api";
import { GenresInput } from "./GenresInput";
import { useToast } from "@/app/providers/Toaster";

/**
 * RegisterArtistModal — alta manual DIRECTA de un artista desde Nexus.
 *
 * Espejo de RegisterProducerModal. A diferencia de la postulación/invitación
 * (email + wizard), esto deja al artista activo al instante con su perfil de
 * catálogo armado: solo tiene que entrar a su portal con su cuenta (Google o
 * email). Si el email no existe, el backend crea la cuenta; si ya existe, la
 * promueve. Idempotente.
 *
 * Ocultar el botón que lo abre no es seguridad — el backend exige
 * ARTISTS_MGMT_EDIT igual. Este modal es solo la UX.
 */

// Catálogo de países (ISO alpha-2). Mixcore opera sobre todo en Chile, pero
// el catálogo de artistas es regional. Default CL por conveniencia.
const COUNTRIES: { value: string; label: string }[] = [
  { value: "", label: "Sin especificar" },
  { value: "CL", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "PE", label: "Perú" },
  { value: "CO", label: "Colombia" },
  { value: "MX", label: "México" },
  { value: "UY", label: "Uruguay" },
  { value: "BR", label: "Brasil" },
  { value: "ES", label: "España" },
  { value: "US", label: "Estados Unidos" },
];

// Sugerencias de región para Chile (datalist — no es lista cerrada, el
// artista puede vivir en cualquier país).
const CHILE_REGIONS = [
  "Arica y Parinacota",
  "Tarapacá",
  "Antofagasta",
  "Atacama",
  "Coquimbo",
  "Valparaíso",
  "Metropolitana de Santiago",
  "Libertador General Bernardo O'Higgins",
  "Maule",
  "Ñuble",
  "Biobío",
  "La Araucanía",
  "Los Ríos",
  "Los Lagos",
  "Aysén",
  "Magallanes",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_GENRES = 12;

export function RegisterArtistModal({ onClose }: { onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [artistName, setArtistName] = useState("");
  const [genres, setGenres] = useState<string[]>([]);
  const [bio, setBio] = useState("");
  const [country, setCountry] = useState("CL");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [soundcloudUrl, setSoundcloudUrl] = useState("");
  const [beatportUrl, setBeatportUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [notify, setNotify] = useState(true);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && artistName.trim().length >= 2;

  const createMut = useMutation({
    mutationFn: () =>
      artistsMgmtApi.createManual({
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        artistName: artistName.trim(),
        genres: genres.length ? genres : undefined,
        bio: bio.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
        country: country || undefined,
        region: region.trim() || undefined,
        city: city.trim() || undefined,
        spotifyUrl: spotifyUrl.trim() || undefined,
        soundcloudUrl: soundcloudUrl.trim() || undefined,
        beatportUrl: beatportUrl.trim() || undefined,
        websiteUrl: websiteUrl.trim() || undefined,
        instagramUrl: instagramUrl.trim() || undefined,
        notify,
      }),
    onSuccess: (res) => {
      const who = res.user.email;
      const base = res.wasArtist
        ? `${who} ya era artista. ${res.profileCreated ? "Se completó su ficha." : "No se modificó nada."}`
        : res.created
          ? `Cuenta creada y habilitada: ${who}.`
          : `${who} ahora es artista.`;
      push({ kind: "success", message: base });
      qc.invalidateQueries({ queryKey: ["artists-mgmt"] });
      onClose();
    },
    onError: (err: any) => {
      push({
        kind: "error",
        message:
          err?.response?.data?.error ||
          "No pudimos dar de alta al artista. Intenta de nuevo.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || createMut.isPending) return;
    createMut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="nx-card w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <UserPlus size={16} className="text-accent" />
            <h2 className="text-sm font-semibold text-fg">Registrar artista</h2>
          </div>
          <button
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <p className="text-xs text-fg-muted">
            El artista queda activo al instante con su perfil de catálogo. Si el
            email no tiene cuenta, se crea; luego solo debe entrar a su portal
            con su cuenta de Google o email. No pasa por postulación ni wizard.
          </p>

          {/* ── Cuenta ── */}
          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="artista@email.com"
              className="nx-input w-full"
              autoFocus
            />
            {email.length > 0 && !emailValid && (
              <p className="text-2xs text-danger mt-1">Email inválido.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Nombre</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Nombre real"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Apellido</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Apellido"
                className="nx-input w-full"
              />
            </div>
          </div>
          <p className="text-2xs text-fg-subtle -mt-2">
            Solo se usa si la cuenta es nueva. Si ya existe, se respeta su nombre.
          </p>

          <div className="h-px bg-border-subtle" />

          {/* ── Identidad artística ── */}
          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Nombre artístico <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Alias / nombre de escenario"
              className="nx-input w-full"
            />
          </div>

          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Géneros <span className="text-fg-subtle">· hasta {MAX_GENRES}</span>
            </label>
            <GenresInput value={genres} onChange={setGenres} max={MAX_GENRES} />
          </div>

          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Bio <span className="text-fg-subtle">· opcional</span>
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Breve descripción del artista para su perfil público."
              rows={3}
              className="nx-input w-full resize-y"
            />
          </div>

          <div className="h-px bg-border-subtle" />

          {/* ── Ubicación ── */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">País</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="nx-input w-full"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Región</label>
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="Región / Estado"
                className="nx-input w-full"
                list="artist-region-suggestions"
              />
              <datalist id="artist-region-suggestions">
                {CHILE_REGIONS.map((r) => (
                  <option key={r} value={r} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Ciudad</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Ciudad"
                className="nx-input w-full"
              />
            </div>
          </div>

          <div className="h-px bg-border-subtle" />

          {/* ── Música & links ── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Spotify</label>
              <input
                type="text"
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                placeholder="open.spotify.com/artist/…"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">SoundCloud</label>
              <input
                type="text"
                value={soundcloudUrl}
                onChange={(e) => setSoundcloudUrl(e.target.value)}
                placeholder="soundcloud.com/…"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Beatport</label>
              <input
                type="text"
                value={beatportUrl}
                onChange={(e) => setBeatportUrl(e.target.value)}
                placeholder="beatport.com/artist/…"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Instagram</label>
              <input
                type="text"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="instagram.com/…"
                className="nx-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Web</label>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="tusitio.com"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Imagen (URL)</label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…/foto.jpg"
                className="nx-input w-full"
              />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 accent-[var(--accent,#F0354D)]"
            />
            <span className="text-xs text-fg-muted">
              Enviar email de bienvenida con el link al portal de artista.
            </span>
          </label>
        </form>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
          <button type="button" onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || createMut.isPending}
            className="nx-btn-primary disabled:opacity-40"
          >
            {createMut.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Dando de alta…
              </>
            ) : (
              <>
                <UserPlus size={14} />
                Registrar artista
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
